import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import classnames from 'classnames';
import { Editor, EditorState, ContentState, convertToRaw, Modifier } from 'draft-js';
import SingleLineEditor from './SingleLineEditor';

import './_TextFieldCore.scss';

import getConditionalClasses, {
  CORE_CLASSNAME
} from '@adsk/bim360-matrix-react-helpers/es/getConditionalClasses';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

const TEXTFIELD_CORE_PROPS = [
  'value',
  'style',
  'className',
  'conditionalClassNames',
  'behavior',
  'behaviors',
  'onChange',
  'onFocus',
  'onBlur',
  'valid',
  'disabled',
  'readOnly',
  'stripPastedStyles',
  'autoselect',
  'autofocus',
  'spellcheck',
  'data-testid'
];

/**
 * @class TextFieldCore
 * Same thing as `TextField` but leaner (might be meaner, and definitely is a texting machine).
 * While actually doing less, it allows us to do more.
 * Only controls the behavior of how the text flows within it, everything else is a bonus (InlineEditTextField, FloatingLabelTextField).
 *
 * If you want to wrap this component as a new kind of text field, make sure you reset the css states of this component,
 * using `text-field-core-states` or `text-field-core-border-states` mixins (at the very least do `@include text-field-core-border-states(none)`).
 * You can look at FloatingLabelTextField/InlineEditTextField.scss for the usage.
 **/
export default class TextFieldCore extends PureComponent {
  /**
   * @constructor TextFieldCore
   **/
  constructor(props) {
    super(props);

    this.state = {
      focused: false,
      editorState: EditorState.createWithContent(ContentState.createFromText(props.value || '')),
      behavior: this._getBehavior(props)
    };

    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.setEditorRef = this.setEditorRef.bind(this);
    this.forceFocusOnBoxClick = this.forceFocusOnBoxClick.bind(this);

    this.focus = this.focus.bind(this);
    this.blur = this.blur.bind(this);
    this.select = this.select.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // The order of the if checks matters.
    // Since at this point setState is synchronous changing this.state.behavior changes the result of this._getText.
    if (
      nextProps.behavior !== this.props.behavior ||
      nextProps.behaviors !== this.props.behaviors
    ) {
      this.setState({ behavior: this._getBehavior(nextProps) });
    }

    const nextValue = nextProps.value || '';
    if (nextValue !== this._getText(this.state.editorState)) {
      this.setState({
        editorState: EditorState.push(
          this.state.editorState,
          ContentState.createFromText(nextValue)
        )
      });
    }
  }

  componentDidMount() {
    if (this.props.autoselect) {
      this.select();
    } else if (this.props.autofocus) {
      this.focus();
    }
  }

  onChange(editorState) {
    const editorStateText = this._getText(editorState);
    const shouldCallOnChange = editorStateText !== this._getText(this.state.editorState);
    this.setState({ editorState }, () => {
      shouldCallOnChange && this.props.onChange(editorStateText);
    });
  }

  onFocus(...args) {
    this.focused = true;
    this.setState({ focused: true });
    this.props.onFocus(...args);
  }

  onBlur(...args) {
    this.focused = false;
    this.setState({ focused: false });
    this.props.onBlur(this._getText(this.state.editorState), ...args);
  }

  _scrollHorizontallyOnPaste(editorState) {
    // Scroll to the end of where the text has been pasted.
    // This assumes we're in SINGLE_LINE ('.public-DraftStyleDefault-block' selector) behavior.
    requestAnimationFrame(() => {
      // eslint-disable-next-line react/no-find-dom-node
      const editorNode = findDOMNode(this.editor);
      const scrollingContainer = editorNode.querySelector('.public-DraftStyleDefault-block');
      const context = document.createElement('canvas').getContext('2d');
      context.font = window.getComputedStyle(editorNode).font;

      const textUpToEndOfPaste = this._getText(editorState).substring(
        0,
        editorState.getSelection().getFocusOffset()
      );
      const pastedTextWidthOffset = context.measureText(textUpToEndOfPaste);

      scrollingContainer.scrollLeft = pastedTextWidthOffset.width - editorNode.clientWidth / 2;
    });
  }

  _handlePaste(postChange = () => {}, modifyText = text => text) {
    return text => {
      const newState = EditorState.push(
        this.state.editorState,
        Modifier.replaceWithFragment(
          this.state.editorState.getCurrentContent(),
          this.state.editorState.getSelection(),
          ContentState.createFromText(modifyText(text)).blockMap
        ),
        'insert-fragment'
      );

      this.onChange(newState);
      postChange(newState);

      return 'handled';
    };
  }

  _handleSingleLinePaste() {
    return this._handlePaste(
      editorState => this._scrollHorizontallyOnPaste(editorState),
      text => text.replace(/\n/g, ' ')
    );
  }

  _handleBreakLinePaste() {
    return this._handlePaste();
  }

  _handleMultiLinePaste() {
    return this._handlePaste();
  }

  _getBehavior(props) {
    const behavior = props.behaviors[props.behavior];
    return {
      handlers: Object.keys(behavior.handlers).reduce((handlers, handlerKey) => {
        handlers[handlerKey] =
          typeof behavior.handlers[handlerKey] === 'string'
            ? this[behavior.handlers[handlerKey]].bind(this)
            : behavior.handlers[handlerKey];
        return handlers;
      }, {}),
      getText: (...args) => behavior.getText(...args),
      modifierClassName: behavior.modifierClassName,
      editor: behavior.editor
    };
  }

  _getText(editorState) {
    return this.state.behavior.getText(
      convertToRaw(editorState.getCurrentContent())
        .blocks.map(_ => _.text)
        .join('\n')
    );
  }

  // Making sure that we ensure a state change has occurred on the text field.
  // We do this because some browsers (ahmIE11ahm) don't trigger event handlers when triggering them manually.
  // We do the double rAF method to make sure that one frame has rendered,
  // that probably tells us if the state has actually changed.
  _ensureState(forceState) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        forceState();
      });
    });
  }

  focus() {
    if (this.editor) {
      this.editor.focus();
      this._ensureState(() => !this.focused && this.onFocus());
    }
  }

  blur() {
    if (this.editor) {
      this.editor.blur();
      this._ensureState(() => this.focused && this.onBlur());
    }
  }

  select() {
    // In case this is called in componentDidMount we need to wait a bit in order for the DOM to "stabilize" for IE11 of course.
    setTimeout(() => {
      let editorNode;
      try {
        // eslint-disable-next-line react/no-find-dom-node
        editorNode = findDOMNode(this.editor);
      } catch (e) {
        return;
      }
      if (editorNode.select) {
        editorNode.select();
      } else {
        this.focus();
        if (this._getText(this.state.editorState)) {
          const editorState = EditorState.moveFocusToEnd(this.state.editorState);
          const allSelectionState = editorState
            .getSelection()
            .set(
              'anchorKey',
              editorState
                .getCurrentContent()
                .getFirstBlock()
                .getKey()
            )
            .set('anchorOffset', 0);
          this.setState({
            editorState: EditorState.forceSelection(editorState, allSelectionState)
          });
        }
      }
    });
  }

  setEditorRef(ref) {
    if (ref && this.editor !== ref) {
      this.editor = ref;
    }
  }

  forceFocusOnBoxClick() {
    if (!this.state.focused && this.editor) {
      this.editor.focus();
    }
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const coreClasses = ['TextFieldCore', this.props.className];
    const Input = this.state.behavior.editor || Editor;

    const classes = classnames(
      coreClasses,
      getConditionalClasses(coreClasses, {
        ...this.props.conditionalClassNames,
        [`${CORE_CLASSNAME}--focused`]: this.state.focused,
        [`${CORE_CLASSNAME}--invalid`]: !this.props.valid,
        [`${CORE_CLASSNAME}--disabled`]: this.props.disabled,
        [`${CORE_CLASSNAME}--${this.state.behavior.modifierClassName}`]: true
      })
    );
    return (
      <div
        className={classes}
        style={this.props.style}
        data-testid={this.props['data-testid']}
        onClick={this.forceFocusOnBoxClick}
      >
        <Input
          {...omit(this.props, TEXTFIELD_CORE_PROPS)}
          {...this.state.behavior.handlers}
          stripPastedStyles={this.props.stripPastedStyles}
          tabIndex="0"
          editorState={this.state.editorState}
          ref={this.setEditorRef}
          disabled={this.props.disabled}
          spellCheck={this.props.spellCheck}
          readOnly={this.props.readOnly !== undefined ? this.props.readOnly : this.props.disabled}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
        />
      </div>
    );
  }
}

TextFieldCore.Behaviors = {
  SINGLE_LINE: 'single',
  MULTI_LINE: 'multi',
  BREAK_LINE: 'break'
};

TextFieldCore.DefaultBehaviors = {
  [TextFieldCore.Behaviors.SINGLE_LINE]: {
    handlers: {
      // You can uncomment these handlers once we ditch support for IE11
      // handlePastedText: '_handleSingleLinePaste',
      handleReturn: () => 'handled'
    },
    // This editor exists solely for IE11 purposes.
    // see https://github.com/facebook/draft-js/issues/657#issuecomment-335770848
    // We're rendering it in all browsers to remain consistent.
    // If, by any chance, we'll drop support for IE11 we can remove this so we'll always use Draft.js as the editor.

    // Note - We're converting to and from the Draft.js EditorState,
    // although it's slower it saves us the hassle of handling everything specifically for this input.
    editor: SingleLineEditor,
    getText: text => text.replace(/\n/g, ' '),
    modifierClassName: 'single-line'
  },
  [TextFieldCore.Behaviors.BREAK_LINE]: {
    handlers: {
      handleReturn: () => 'handled',
      handlePastedText: '_handleBreakLinePaste'
    },
    getText: text => text.replace(/\n/g, ' '),
    modifierClassName: 'break-line'
  },
  [TextFieldCore.Behaviors.MULTI_LINE]: {
    handlers: {
      handleReturn: undefined,
      handlePastedText: '_handleMultiLinePaste'
    },
    getText: text => text,
    modifierClassName: 'multi-line'
  }
};

TextFieldCore.defaultProps = {
  behavior: TextFieldCore.Behaviors.SINGLE_LINE,
  behaviors: TextFieldCore.DefaultBehaviors,
  conditionalClassNames: {},
  valid: true,
  onFocus: noop,
  onBlur: noop,
  onChange: noop,
  value: '',
  stripPastedStyles: true,
  spellCheck: true
};

TextFieldCore.propTypes = {
  /**
   * Value to display in the input, can be used as an initial value and as a value to reset the input with.
   */
  value: PropTypes.string,
  /**
   * Style object
   */
  style: PropTypes.object,
  /**
   * Core class name that's applied to the container, also get's all the BEM modifiers the input can be in like, "--focused", "--invalid",
   */
  className: PropTypes.string,
  /**
   * Object describing conditional classes to apply on the core classes, useful when you want to extend the basic states of the text field.
   */
  conditionalClassNames: PropTypes.object,
  /**
   * The behavior the input should have, can be one of the default ones (SINGLE_LINE, MULTI_LINE or BREAK_LINE).
   * SINGLE_LINE - Allows for only one line. Just like a regular <input type="text"/>.
   * MULTI_LINE - Allows for multi line content. Just like <textarea />
   * BREAK_LINE - Allows for only one line of content but is broken so that it won't overflow the width but rather create a virtual new line. Think of it as a <input type="text"/> with word-wrap: break-word;
   *
   * Or can be a custom behavior defined by one of the behaviors in this.props.behaviors.
   */
  behavior: PropTypes.oneOf(objectValues(TextFieldCore.Behaviors)),
  /**
   * Custom behaviors to supply in case the basic ones are not enough.
   */
  behaviors: PropTypes.objectOf(
    PropTypes.shape({
      /**
       * If this behavior requires a different kind of editor this is how to supply it.
       * In most cases you probably won't need it, unless you're using draft-js-plugins-editor or something like that.
       */
      editor: PropTypes.func,
      /**
       * An object of handlers that's passed to draft js, see https://draftjs.org/docs/api-reference-editor.html#cancelable-handlers-optional
       */
      handlers: PropTypes.object,
      /**
       * A function that gets the original text and transforms it according to the current behavior.
       * (text) => string
       */
      getText: PropTypes.func.isRequired,
      /**
       * A specific classname to apply as a modifier to the main container of the component.
       */
      modifierClassName: PropTypes.string.isRequired
    })
  ),
  /**
   * Callback that's being called when the content has changed.
   * (text) => {}
   */
  onChange: PropTypes.func,
  /**
   * Callback that's being called when the input receives focus.
   */
  onFocus: PropTypes.func,
  /**
   * Callback that's being called when the input is blurred. First param will be the input value the other params will be the event args.
   */
  onBlur: PropTypes.func,
  /**
   * Flag indicating whether or not the input is in a valid or invalid state.
   */
  valid: PropTypes.bool,
  /**
   * Flag indicating whether or not the input is in a disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * Flag indicating whether or not the input is readOnly. Not needed when using `disabled` prop.
   */
  readOnly: PropTypes.bool,
  /**
   * Auto focuses the component on mount
   */
  autofocus: PropTypes.bool,
  /**
   * Auto selects the contents of the text field on mount
   */
  autoselect: PropTypes.bool,
  /**
   * Flag indicating whether or not the input will strip pasted styles.
   */
  stripPastedStyles: PropTypes.bool,
  /**
   * Flag indicating whether or not the input will check spelling (using browser's implementation).
   */
  spellCheck: PropTypes.bool,
  /**
   *  Support for data-testid attributes for internal automation.
   */
  'data-testid': PropTypes.string
};
